import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { Modal } from "app/components/Modal";
import { DropdownIcon } from "app/icons";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import {
  ChassisHeightSelectOption,
  MANUFACTURER_OPTIONS,
  MASTER_MODEL_OPTIONS,
  SUSPENSION_TYPE_OPTIONS,
} from "../../data";
import {
  selectChassisHeightForm,
  selectChassisHeightSaving,
  selectIsAddModalOpen,
} from "../../redux/selector";
import { chassisHeightActions } from "../../redux/slice";
import { ChassisHeightForm } from "../../types";
import { useStyles } from "./styles";

type FieldConfig = {
  key: keyof ChassisHeightForm;
  label: string;
  type: "select" | "text";
  options?: ChassisHeightSelectOption[];
};

const ROWS: FieldConfig[][] = [
  [
    {
      key: "manufacturer",
      label: "MANUFACTURER",
      type: "select",
      options: MANUFACTURER_OPTIONS,
    },
    {
      key: "masterModel",
      label: "MASTER_MODEL",
      type: "select",
      options: MASTER_MODEL_OPTIONS,
    },
  ],
  [
    {
      key: "suspensionType",
      label: "SUSPENSION_TYPE",
      type: "select",
      options: SUSPENSION_TYPE_OPTIONS,
    },
    { key: "axleDistance", label: "AXLE_DISTANCE", type: "text" },
  ],
  [
    { key: "tire", label: "TIRE", type: "text" },
    { key: "year", label: "YEAR", type: "text" },
  ],
  [
    { key: "net", label: "NET", type: "text" },
    {
      key: "chassisHeightIncludingBody",
      label: "CHASSIS_HEIGHT_INCLUDING_BODY",
      type: "text",
    },
  ],
];

const REQUIRED_FIELDS: Array<keyof ChassisHeightForm> = [
  "manufacturer",
  "masterModel",
  "suspensionType",
  "axleDistance",
  "tire",
  "year",
  "net",
  "chassisHeightIncludingBody",
];

const P = "CHASSIS_HEIGHT.MODAL";

const AddChassisHeightModal = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const dispatch = useDispatch();
  const open = useSelector(selectIsAddModalOpen);
  const form = useSelector(selectChassisHeightForm);
  const saving = useSelector(selectChassisHeightSaving);

  const onClose = () => {
    if (saving) return;
    dispatch(chassisHeightActions.closeAddModal());
  };

  const setField = (name: keyof ChassisHeightForm, value: string) =>
    dispatch(chassisHeightActions.setFormField({ name, value }));

  const placeholder = (label: string, type: "select" | "text") =>
    t(`${P}.PLACEHOLDERS.${type === "select" ? "SELECT" : "ENTER"}_${label}`);

  const isValid = REQUIRED_FIELDS.every((key) => Boolean(form[key].trim()));

  const optionLabel = (option: ChassisHeightSelectOption) =>
    option.labelKey ? t(option.labelKey) : option.label || option.value;

  const renderField = ({ key, label, type, options = [] }: FieldConfig) => (
    <Box key={key} className={styles.field}>
      <Typography className={styles.label}>
        {t(`${P}.FIELDS.${label}`)}
      </Typography>
      <TextField
        className={styles.input}
        fullWidth
        select={type === "select"}
        variant="outlined"
        placeholder={placeholder(label, type)}
        value={form[key]}
        disabled={saving}
        InputProps={{ notched: false }}
        SelectProps={
          type === "select"
            ? {
                displayEmpty: true,
                IconComponent: DropdownIcon,
                renderValue: (value) => {
                  const selected = options.find(
                    (option) => option.value === value
                  );
                  return selected
                    ? optionLabel(selected)
                    : placeholder(label, "select");
                },
              }
            : undefined
        }
        onChange={(event) => setField(key, event.target.value)}
      >
        {type === "select"
          ? [
              <MenuItem key="empty" value="" disabled>
                {placeholder(label, "select")}
              </MenuItem>,
              ...options.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {optionLabel(option)}
                </MenuItem>
              )),
            ]
          : undefined}
      </TextField>
    </Box>
  );

  return (
    <Modal open={open} onClose={onClose}>
      <Box className={styles.modal}>
        <Box className={styles.header}>
          <Typography className={styles.title}>{t(`${P}.ADD_TITLE`)}</Typography>
          <IconButton
            className={styles.closeButton}
            onClick={onClose}
            aria-label={t("BUTTONS.CANCEL")}
            disabled={saving}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box className={styles.body}>
          {ROWS.map((row, index) => (
            <Box key={index} className={styles.grid}>
              {row.map(renderField)}
            </Box>
          ))}
        </Box>

        <Box className={styles.footer}>
          <Button
            className={styles.cancelButton}
            variant="outlined"
            onClick={onClose}
            disabled={saving}
          >
            {t("BUTTONS.CANCEL")}
          </Button>
          <Button
            className={styles.addButton}
            variant="contained"
            disableElevation
            disabled={!isValid || saving}
            onClick={() => dispatch(chassisHeightActions.addChassisHeight())}
          >
            {t("BUTTONS.ADD")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddChassisHeightModal;
